import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, Alert } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import AnimatedLottieView from '@utils/lottieAnimation';
import apis from '@api/api';
import { Spinner, Card, YStack, XStack } from 'tamagui';
import { debounce, set } from 'lodash';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import styles from '@stylesheet';
import { Title } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface InventoryManagementProps {
  dataId: string;
}

const InventoryManagement = ({ dataId }: InventoryManagementProps) => {
  const theme = themeSelector();
  const [inventoryData, setInventoryData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [availableWidth, setAvailableWidth] = useState(0);

  // Fetch inventory data using the new product API
  const { data: inventoryDataResponse, error, isLoading, refetch } = useQuery({
    queryKey: ['productInventory', dataId],
    queryFn: async () => {
      try {
        const response = await apis.getProductInventory(dataId);
        if (response && response.success) {
          setInventoryData(response.inventory);
          return response.inventory;
        }
        return null;
      } catch (error) {
        console.error('Error fetching inventory data:', error);
        return null;
      }
    },
    enabled: !!dataId
  });

  // Update local state when query data changes
  useEffect(() => {
    if (inventoryDataResponse) {
      setInventoryData(inventoryDataResponse);
    }
  }, [inventoryDataResponse]);

  const handleInventoryUpdate = async (formData: any) => {
    setLoading(true);
    try {
      const inventoryUpdate = {
        currentStock: formData.stock || formData.quantity || 0,
        minStock: formData.minStock || 10,
        maxStock: formData.maxStock || 1000,
        reorderPoint: formData.reorderPoint || 20,
        lastUpdated: new Date().toISOString(),
        notes: formData.inventoryNotes || ''
      };

      const response = await apis.updateProductInventory(dataId, inventoryUpdate);
      
      if (response?.success) {
        Alert.alert('Success', 'Inventory updated successfully');
        setInventoryData(prevData => ({ ...prevData, ...formData }));
        refetch();
      } else {
        Alert.alert('Error', 'Failed to update inventory');
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      Alert.alert('Error', 'Failed to update inventory');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <View style={{ 
        borderRadius: 20, 
        shadowRadius: 20, 
        shadowOpacity: 0.2, 
        alignContent: 'center', 
        justifyContent: 'center', 
        alignItems: 'center', 
        overflow: 'hidden', 
        alignSelf: 'center', 
        top: 300, 
        flex: 1, 
        maxHeight: 200, 
        maxWidth: 200 
      }}>
        <AnimatedLottieView 
          source={require('@assets/animations/leaf_Loader.json')} 
          style={{ flex: 1 }} 
          autoPlay 
          loop 
        />
        <Spinner />
      </View>
    );
  }

  return (
    <YStack flex={1} padding="$4" gap="$4">
      {/* Current Inventory Status */}
      <Card padding="$4" backgroundColor={theme ? '$background' : '$backgroundTransparent'} borderRadius="$4">
        <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>Current Inventory Status</Text>
        
        <XStack gap="$4" flexWrap="wrap">
          <YStack flex={1} minWidth="150">
            <Text style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Current Stock</Text>
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: 'rgb(90,192,234)' }}>
              {inventoryData?.stock || inventoryData?.quantity || 0}
            </Text>
          </YStack>
          
          <YStack flex={1} minWidth="150">
            <Text style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Min. Stock Level</Text>
            <Text style={{ fontSize: 16, fontWeight: '500' }}>
              {inventoryData?.minStock || 'Not set'}
            </Text>
          </YStack>
          
          <YStack flex={1} minWidth="150">
            <Text style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Reorder Point</Text>
            <Text style={{ fontSize: 16, fontWeight: '500' }}>
              {inventoryData?.reorderPoint || 'Not set'}
            </Text>
          </YStack>
        </XStack>

        {/* Stock Status Indicator */}
        <YStack marginTop="$3">
          <Text style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Stock Status</Text>
          <XStack alignItems="center" gap="$2">
            <View style={{
              width: 12,
              height: 12,
              borderRadius: 6,
              backgroundColor: 
                (inventoryData?.stock || 0) <= (inventoryData?.reorderPoint || 10) ? 'rgb(239,68,68)' :
                (inventoryData?.stock || 0) <= (inventoryData?.minStock || 20) ? 'rgb(251,146,60)' : 'rgb(34,197,94)'
            }} />
            <Text style={{ fontWeight: '500' }}>
              {(inventoryData?.stock || 0) <= (inventoryData?.reorderPoint || 10) ? 'Critical - Reorder Now' :
               (inventoryData?.stock || 0) <= (inventoryData?.minStock || 20) ? 'Low Stock - Monitor' : 'Good Stock Level'}
            </Text>
          </XStack>
        </YStack>
      </Card>

      {/* Update Inventory Form */}
      <Card padding="$4" backgroundColor={theme ? '$background' : '$backgroundTransparent'} borderRadius="$4">
        <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 16 }}>Update Inventory</Text>
        
        <View style={{ flex: 1, alignSelf: "stretch", minWidth: "100%" }} 
              onLayout={({ nativeEvent }) => debounce(() => setAvailableWidth(nativeEvent.layout.width), 500)}>
          
          <GenerateSchemaForm
            noBackground
            schema={{
              type: 'object',
              properties: {
                stock: {
                  type: 'number',
                  title: 'Current Stock',
                  description: 'Current quantity in inventory'
                },
                minStock: {
                  type: 'number',
                  title: 'Minimum Stock Level',
                  description: 'Minimum quantity to maintain'
                },
                maxStock: {
                  type: 'number',
                  title: 'Maximum Stock Level',
                  description: 'Maximum quantity to store'
                },
                reorderPoint: {
                  type: 'number',
                  title: 'Reorder Point',
                  description: 'Stock level that triggers reorder'
                },
                inventoryNotes: {
                  type: 'string',
                  title: 'Notes',
                  description: 'Additional inventory notes'
                }
              }
            }}
            containerWidth={availableWidth}
            avoidKeyboardDisabled
            buttonLabel={loading ? 'Updating...' : 'Update Inventory'}
            customStyling={{ flex: 1, alignSelf: 'center', alignContent: 'center' }}
            buttonStyle={[
              styles.LoginButton, 
              { 
                alignSelf: 'center', 
                maxHeight: 40, 
                borderColor: 'rgb(90,192,234)', 
                backgroundColor: loading ? '#ccc' : 'rgb(90,192,234)', 
                marginVertical: 16 
              }
            ]}
            submitFunction={handleInventoryUpdate}
            retainInitialValues
            internalButton={false}
            initialData={inventoryData || {}}
            disabled={loading}
          />
        </View>
      </Card>

      {/* Quick Actions */}
      <XStack gap="$3" flexWrap="wrap">
        <Pressable
          style={{
            flex: 1,
            padding: 12,
            backgroundColor: 'rgb(34,197,94)',
            borderRadius: 8,
            alignItems: 'center',
            minWidth: 120
          }}
          onPress={() => {
            Alert.prompt(
              'Add Stock',
              'Enter quantity to add:',
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Add',
                  onPress: (value) => {
                    const addAmount = parseInt(value || '0');
                    if (addAmount > 0) {
                      const newStock = (inventoryData?.stock || 0) + addAmount;
                      handleInventoryUpdate({ ...inventoryData, stock: newStock });
                    }
                  }
                }
              ],
              'plain-text',
              '',
              'numeric'
            );
          }}
        >
          <Text style={{ color: 'white', fontWeight: '600' }}>+ Add Stock</Text>
        </Pressable>

        <Pressable
          style={{
            flex: 1,
            padding: 12,
            backgroundColor: 'rgb(239,68,68)',
            borderRadius: 8,
            alignItems: 'center',
            minWidth: 120
          }}
          onPress={() => {
            Alert.prompt(
              'Remove Stock',
              'Enter quantity to remove:',
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Remove',
                  onPress: (value) => {
                    const removeAmount = parseInt(value || '0');
                    if (removeAmount > 0) {
                      const newStock = Math.max(0, (inventoryData?.stock || 0) - removeAmount);
                      handleInventoryUpdate({ ...inventoryData, stock: newStock });
                    }
                  }
                }
              ],
              'plain-text',
              '',
              'numeric'
            );
          }}
        >
          <Text style={{ color: 'white', fontWeight: '600' }}>- Remove Stock</Text>
        </Pressable>
      </XStack>
    </YStack>
  );
};

export default InventoryManagement;